import { NgClass } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MenuItem, MessageService } from 'primeng/api';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { Menu } from 'primeng/menu';
import { Textarea } from 'primeng/textarea';
import { Toast } from 'primeng/toast';
import { Subscription } from 'rxjs';

import { AvisoActivarPushComponent } from '../../shared/components/aviso-activar-push/aviso-activar-push.component';
import { ChatHeaderComponent } from '../../shared/components/chat/chat-header/chat-header.component';
import { ChatInputComponent } from '../../shared/components/chat/chat-input/chat-input.component';
import { ChatLayoutComponent } from '../../shared/components/chat/chat-layout/chat-layout.component';
import { ChatListComponent } from '../../shared/components/chat/chat-list/chat-list.component';
import { ChatMessagesComponent } from '../../shared/components/chat/chat-messages/chat-messages.component';
import { ordenarConversaciones } from '../../shared/helpers/chat-orden';
import { chatTieneSolicitud } from '../../shared/helpers/chat-tiene-pendiente';
import { ChatConversacion } from '../../shared/interfaces/chat.interface';
import { CelulaDataSource } from './celula-data-source';
import { CelulaService } from './celula.service';
import { CelulaPedido, celulaAvisos, pedidoEliminar, pedidosDisponibles } from './celula.model';

@Component({
  selector: 'app-lider',
  standalone: true,
  imports: [
    FormsModule,
    NgClass,
    Button,
    Dialog,
    Menu,
    Textarea,
    Toast,
    AvisoActivarPushComponent,
    ChatLayoutComponent,
    ChatListComponent,
    ChatHeaderComponent,
    ChatMessagesComponent,
    ChatInputComponent,
  ],
  providers: [{ provide: CelulaDataSource, useClass: CelulaService }],
  templateUrl: './lider.component.html',
  styleUrl: './lider.component.scss',
})
export class LiderComponent implements OnInit, OnDestroy {
  private readonly dataSource = inject(CelulaDataSource);
  private readonly messages = inject(MessageService);
  private readonly router = inject(Router);
  private readonly subscriptions = new Subscription();

  readonly conversaciones = signal<ChatConversacion[]>([]);
  readonly seleccionadaId = signal<string | null>(null);
  readonly loading = signal(true);
  readonly detalleAbierto = signal(false);
  readonly setLevel = signal(0);
  readonly cantCulto = signal(1);

  readonly motivoVisible = signal(false);
  readonly motivoTexto = signal('');
  readonly pedidoActivo = signal<CelulaPedido | null>(null);

  readonly seleccionada = computed(
    () =>
      this.conversaciones().find(
        (conversacion) => conversacion.personaId === this.seleccionadaId(),
      ) ?? null,
  );

  readonly mostrarNivel = computed(() => this.setLevel() !== 0);

  readonly avisos = computed(() => {
    const conversacion = this.seleccionada();
    return conversacion ? celulaAvisos(conversacion, this.cantCulto()) : [];
  });

  readonly menuItems = computed((): MenuItem[] => {
    const conversacion = this.seleccionada();
    if (!conversacion) {
      return [];
    }

    const id = conversacion.personaId;
    const nombre = `${conversacion.nombre} ${conversacion.apellido}`.trim();
    const pedidos = pedidosDisponibles(conversacion, this.setLevel()).map((pedido) => ({
      label: pedido.label,
      icon: pedido.icono,
      command: () => this.abrirPedido(pedido),
    }));
    const eliminar = chatTieneSolicitud(conversacion, 'eliminar')
      ? []
      : [
          { separator: true },
          {
            label: 'Eliminar',
            icon: 'pi pi-trash',
            command: () => this.abrirPedido(pedidoEliminar(nombre)),
          },
        ];

    return [
      {
        label: 'Ver perfil',
        icon: 'pi pi-user',
        command: () => this.router.navigate(['/perfil', id]),
      },
      {
        label: 'Editar',
        icon: 'pi pi-pencil',
        command: () => this.router.navigate(['/editar-persona', id]),
      },
      ...pedidos,
      ...eliminar,
    ];
  });

  readonly motivoInvalido = computed(() => this.motivoTexto().trim().length === 0);

  ngOnInit(): void {
    this.subscriptions.add(
      this.dataSource.contexto().subscribe({
        next: (contexto) => {
          this.setLevel.set(contexto.setLevel);
          this.cantCulto.set(contexto.cantCulto);
        },
        error: () => {
          this.setLevel.set(0);
          this.cantCulto.set(1);
        },
      }),
    );
    this.subscriptions.add(
      this.dataSource.listar().subscribe({
        next: (lista) => {
          this.aplicar(lista);
          this.loading.set(false);
        },
        error: (err: HttpErrorResponse) => {
          this.loading.set(false);
          this.avisarError(err, 'No se pudo cargar la célula.');
        },
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  onSeleccionar(personaId: string): void {
    this.seleccionadaId.set(personaId);
    this.detalleAbierto.set(true);
    this.subscriptions.add(
      this.dataSource.marcarLeidos(personaId).subscribe((lista) => this.conversaciones.set(lista)),
    );
  }

  onVolver(): void {
    this.detalleAbierto.set(false);
  }

  onEnviar(nota: string): void {
    const personaId = this.seleccionadaId();
    if (!personaId) {
      return;
    }

    this.subscriptions.add(
      this.dataSource
        .enviarMensaje(personaId, nota)
        .subscribe((lista) => this.conversaciones.set(lista)),
    );
  }

  abrirPedido(pedido: CelulaPedido): void {
    this.pedidoActivo.set(pedido);
    this.motivoTexto.set('');
    this.motivoVisible.set(true);
  }

  confirmarMotivo(): void {
    const conversacion = this.seleccionada();
    const pedido = this.pedidoActivo();
    if (!conversacion || !pedido || this.motivoInvalido()) {
      return;
    }

    const motivo = this.motivoTexto().trim();
    const exito = pedido.exito;
    this.cerrarMotivo();

    this.subscriptions.add(
      this.dataSource.crearSolicitud(conversacion.personaId, pedido.tipo, motivo).subscribe({
        next: (lista) => {
          this.aplicar(lista);
          this.messages.add({ severity: 'success', summary: 'Listo', detail: exito });
        },
        error: (err: HttpErrorResponse) => this.avisarError(err, 'No se pudo enviar la solicitud.'),
      }),
    );
  }

  cerrarMotivo(): void {
    this.motivoVisible.set(false);
    this.motivoTexto.set('');
    this.pedidoActivo.set(null);
  }

  onMotivoVisibleChange(visible: boolean): void {
    if (!visible) {
      this.cerrarMotivo();
    }
  }

  private aplicar(lista: ChatConversacion[]): void {
    this.conversaciones.set(lista);
    const actual = this.seleccionadaId();
    if (actual && lista.some((conversacion) => conversacion.personaId === actual)) {
      return;
    }
    this.seleccionadaId.set(ordenarConversaciones(lista)[0]?.personaId ?? null);
  }

  private avisarError(err: HttpErrorResponse, fallback: string): void {
    const message = err.error?.message;
    this.messages.add({
      severity: 'error',
      summary: 'Error',
      detail: typeof message === 'string' && message.trim() ? message : fallback,
    });
  }
}
